"use client";

import { keepPreviousData } from "@tanstack/react-query";
import { useMetaPlatform } from "./provider";

// --- Ad Account ---

export function useMetaAdAccounts() {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.organization.getOrganizationWithAdAccounts.useQuery(
    { slug: orgSlug },
    { enabled: true },
  );
}

export function useAdAccountBillingStatus(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getAdAccountBillingStatus.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

// --- Pages ---

export function useMetaPages(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getMetaPages.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

// 5-minute stale time. IG identity bonds and BM ownership rarely
// change mid-session, so refetching on every window focus/mount
// (TanStack's default) wastes Graph API quota and adds noticeable
// re-render jank in pickers that read these hooks. Callers can still
// override per-call by passing query options through useQuery's
// merge — but the safe default belongs here, not at every call site.
const IG_QUERY_STALE_MS = 5 * 60 * 1000;

export function useMetaPageInstagramStatus(
  accountId?: string,
  pageId?: string,
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getMetaPageInstagramStatus.useQuery(
    { accountId: accountId!, pageId: pageId!, slug: orgSlug },
    { enabled: !!accountId && !!pageId, staleTime: IG_QUERY_STALE_MS },
  );
}

export function useMetaInstagramAccounts(accountId?: string, pageId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.listMetaInstagramAccounts.useQuery(
    { accountId: accountId!, pageId, slug: orgSlug },
    { enabled: !!accountId, staleTime: IG_QUERY_STALE_MS },
  );
}

// --- Pixels ---

export function useMetaPixels(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getMetaPixels.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

export function usePixelCustomEvents(accountId?: string, pixelId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getPixelCustomEvents.useQuery(
    { accountId: accountId!, pixelId: pixelId!, slug: orgSlug },
    { enabled: !!accountId && !!pixelId },
  );
}

/**
 * Custom conversions on a pixel ({ id, name, customEventType }), offered as
 * optimization targets in their own right.
 */
export function usePixelCustomConversions(
  accountId?: string,
  pixelId?: string,
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getPixelCustomConversions.useQuery(
    { accountId: accountId!, pixelId: pixelId!, slug: orgSlug },
    // Pages through every conversion on the account; they change rarely.
    { enabled: !!accountId && !!pixelId, staleTime: 5 * 60 * 1000 },
  );
}

/**
 * Fetch ALL event names (standard + custom) that have actually fired on a pixel,
 * mapped to UPPER_SNAKE_CASE MetaConversionEvent enum values. Used to group
 * events into Active/Inactive in the Campaign Setup card.
 */
export function usePixelActiveEvents(accountId?: string, pixelId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getPixelActiveEvents.useQuery(
    { accountId: accountId!, pixelId: pixelId!, slug: orgSlug },
    { enabled: !!accountId && !!pixelId },
  );
}

/**
 * Reporting preview for a single ad account: aggregated insights across the
 * org's recent campaigns. Includes `customConversions[]` and `customActions[]`
 * with `.value` (usage count) — used to surface non-ODAX events in the
 * monitoring dropdown and to rank events by "most used first".
 *
 * `organizationId` is required because the underlying admin endpoint is
 * org-scoped (handles agency vs. brand auth differently). Callers that only
 * have `orgSlug` should resolve the id via the session/org context.
 */
export function useReportingPreview(
  organizationId?: string,
  accountId?: string,
) {
  const { trpc } = useMetaPlatform();
  return trpc.admin.getReportingPreview.useQuery(
    {
      organizationId: organizationId!,
      adAccountIds: accountId ? [accountId] : [],
    },
    {
      enabled: !!organizationId && !!accountId,
      staleTime: 5 * 60 * 1000,
    },
  );
}

// --- Apps ---

export function useMetaPromotableApps(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getPromotableApps.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

/**
 * Returns which standard app events are actively tracked by the app.
 * Maps Meta's internal names (fb_mobile_purchase) to UPPER_SNAKE enum
 * values (PURCHASE). Used to show Active/Inactive grouping in the app
 * event selector, same pattern as usePixelActiveEvents for pixels.
 */
export function useAppActiveEvents(accountId?: string, appId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getAppActiveEvents.useQuery(
    { accountId: accountId!, appId: appId!, slug: orgSlug },
    { enabled: !!accountId && !!appId },
  );
}

export function useAppCustomEvents(accountId?: string, appId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getAppCustomEvents.useQuery(
    { accountId: accountId!, appId: appId!, slug: orgSlug },
    { enabled: !!accountId && !!appId },
  );
}

// --- Audiences ---

export function useMetaCustomAudiences(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getMetaCustomAudiences.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

export function useMetaSavedAudiences(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getMetaSavedAudiences.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

export function useSearchMetaInterests(accountId?: string, query?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.searchMetaInterests.useQuery(
    { accountId: accountId!, query: query!, slug: orgSlug },
    { enabled: !!accountId && !!query && query.length >= 2 },
  );
}

export function useSearchMetaCountries(accountId?: string, query?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.searchMetaCountries.useQuery(
    { accountId: accountId!, query: query!, slug: orgSlug },
    { enabled: !!accountId && !!query && query.length >= 1 },
  );
}

export function useSearchMetaRegions(
  accountId?: string,
  query?: string,
  countryCodes?: string[],
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.searchMetaRegions.useQuery(
    {
      accountId: accountId!,
      query: query!,
      slug: orgSlug,
      ...(countryCodes?.length ? { countryCodes } : {}),
    },
    { enabled: !!accountId && !!query && query.length >= 1 },
  );
}

// --- Campaigns ---

export function useActiveCampaigns(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getActiveCampaigns.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

export function useSavedAudiencesV2(accountId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getSavedAudiences.useQuery(
    { accountId: accountId!, slug: orgSlug },
    { enabled: !!accountId },
  );
}

export function useAccountCapabilities(accountId?: string, pageId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getAccountCapabilities.useQuery(
    { accountId: accountId!, pageId, slug: orgSlug },
    { enabled: !!accountId },
  );
}

/**
 * Retry policy for the launch-target reuse lists.
 *
 * These endpoints now surface Graph failures instead of returning an empty
 * array, which re-armed React Query's default of three retries. For a rate
 * limit that is actively harmful: one throttled fetch became four calls
 * against an ad account Meta had already cut off. A throttle needs minutes,
 * not milliseconds, and the picker tells the user to come back later — so
 * these codes fail immediately and only genuinely transient errors retry.
 */
const NON_RETRYABLE_REUSE_CODES = new Set([
  "TOO_MANY_REQUESTS",
  "PRECONDITION_FAILED",
  "FORBIDDEN",
  "UNAUTHORIZED",
  "BAD_REQUEST",
]);

export function retryReuseListQuery(
  failureCount: number,
  error: { data?: { code?: string } | null },
): boolean {
  if (NON_RETRYABLE_REUSE_CODES.has(error?.data?.code ?? "")) {
    return false;
  }
  return failureCount < 2;
}

export function useMetaReusableCampaigns(
  accountId?: string,
  options?: { skipObjectiveFilter?: boolean; defaultId?: string },
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.adPickers.listCampaigns.useQuery(
    {
      platform: "META",
      purpose: "reuse",
      accountId,
      slug: orgSlug,
      limit: 100,
      defaultId: options?.defaultId,
      skipObjectiveFilter: options?.skipObjectiveFilter,
    },
    {
      enabled: !!accountId,
      retry: retryReuseListQuery,
      select: (list) =>
        list.purpose === "reuse" && list.platform === "META" ? list.items : [],
    },
  );
}

export function useMetaReusableAdsets(
  accountId?: string,
  campaignId?: string,
  options?: { skipObjectiveFilter?: boolean; defaultId?: string },
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.adPickers.listAdGroups.useQuery(
    {
      platform: "META",
      purpose: "reuse",
      accountId,
      campaignId: campaignId ?? "",
      slug: orgSlug,
      limit: 100,
      defaultId: options?.defaultId,
      skipObjectiveFilter: options?.skipObjectiveFilter,
    },
    {
      enabled: !!accountId && !!campaignId,
      retry: retryReuseListQuery,
      select: (list) =>
        list.purpose === "reuse" && list.platform === "META" ? list.items : [],
    },
  );
}

// --- Placements ---

export function useMetaAdCampaignPlacements(params?: {
  accountId: string;
  objective?: string;
  optimizationGoal?: string;
  billingEvent?: string;
}) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.meta.getAdCampaignPlacements.useQuery(
    {
      accountId: params?.accountId!,
      objective: params?.objective,
      optimization_goal: params?.optimizationGoal,
      billing_event: params?.billingEvent,
      slug: orgSlug,
    },
    { enabled: !!params?.accountId },
  );
}

// --- Copy Presets ---

/**
 * Named headline + primary-text bundles scoped to a CreativeTestingDefault.
 * Loaded into the batch form to replace current copy with a saved set.
 * Ordered ASC by createdAt (oldest first) to match v1 behavior.
 */
export function useCopyPresets(creativeTestingDefaultId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.organization.listCopyPresets.useQuery(
    { creativeTestingDefaultId: creativeTestingDefaultId!, slug: orgSlug },
    { enabled: !!creativeTestingDefaultId },
  );
}

// --- Batch Status ---

export function useGetBatchStatus(
  batchId?: string,
  options?: { pollInterval?: number },
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getBatchStatus.useQuery(
    { batchId: batchId!, slug: orgSlug },
    {
      enabled: !!batchId,
      refetchInterval: (query: {
        state: { data: { status?: string } | undefined };
      }) => {
        const data = query.state.data;
        if (
          data?.status === "ACTIVE" ||
          data?.status === "FAILED" ||
          data?.status === "PARTIALLY_FAILED"
        ) {
          return false;
        }
        return options?.pollInterval ?? 2000;
      },
      refetchOnWindowFocus: false,
    },
  );
}

export function useGetBatchAdsStatus(
  batchId?: string,
  options?: { pollInterval?: number; enabled?: boolean },
) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.creativeTestingRouter.getBatchAdsStatus.useQuery(
    { batchId: batchId!, slug: orgSlug },
    {
      enabled: options?.enabled !== false && !!batchId,
      refetchInterval: (query: {
        state: { data: { status?: string } | undefined };
      }) => {
        const data = query.state.data;
        if (
          data?.status === "ACTIVE" ||
          data?.status === "FAILED" ||
          data?.status === "PARTIALLY_FAILED"
        ) {
          return false;
        }
        return options?.pollInterval ?? 2000;
      },
      refetchOnWindowFocus: false,
    },
  );
}

// --- Audience Estimate ---

export interface AudienceEstimateResult {
  lowerBound: number;
  upperBound: number;
  dailyActive: number;
  ready: boolean;
}

export function useAudienceEstimate(
  accountId?: string,
  targetingSpec?: Record<string, unknown>,
  optimizationGoal?: string,
) {
  const { trpc, orgSlug } = useMetaPlatform();
  const hasTargeting = !!targetingSpec && Object.keys(targetingSpec).length > 0;
  return trpc.creativeTestingRouter.getAudienceEstimate.useQuery(
    {
      accountId: accountId!,
      targetingSpec: targetingSpec!,
      optimizationGoal,
      slug: orgSlug,
    },
    {
      enabled: !!accountId && hasTargeting,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    },
  );
}
